// src/modules/goldVault/pages/InventoryExplorer.jsx
// Universal search: case, transfer, package, seal, batch, dispatch, customer.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Input, Button, Card, Typography, List, Tag, Spin, Empty, Space,
} from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { explorerSearch } from '../api';

const { Title, Text } = Typography;

const TYPE_COLOR = {
  WALK_IN_CASE: 'blue', PLEDGE_CASE: 'orange', TRANSFER: 'purple',
  PACKAGE: 'cyan', BATCH: 'green', DISPATCH: 'gold', CUSTOMER: 'default',
};

const TYPE_LABEL = {
  WALK_IN_CASE: 'Walk-In Case', PLEDGE_CASE: 'Pledge Case', TRANSFER: 'Transfer',
  PACKAGE: 'Package', BATCH: 'Batch', DISPATCH: 'Dispatch', CUSTOMER: 'Customer',
};

export default function InventoryExplorer() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim() || query.trim().length < 2) return;
    setLoading(true);
    setSearched(true);
    try {
      const res = await explorerSearch(query.trim());
      setResults(res.data || []);
    } catch (e) {
      console.error(e);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleNavigate = (result) => {
    switch (result.type) {
      case 'WALK_IN_CASE':
        navigate(`/vault/timeline/${result.id}/walk-in`);
        break;
      case 'PLEDGE_CASE':
        navigate(`/vault/timeline/${result.id}/pledge`);
        break;
      case 'TRANSFER':
        navigate(`/vault/transfers/${result.id}`);
        break;
      case 'BATCH':
        navigate(`/gold-inventory/${result.id}`);
        break;
      case 'DISPATCH':
        navigate(`/smelters/dispatches/${result.id}`);
        break;
      default:
        break;
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>Inventory Explorer</Title>

      <Card size="small" style={{ marginBottom: 24 }}>
        <Space.Compact style={{ width: '100%' }}>
          <Input
            size="large"
            placeholder="Search by Case No., Customer, Phone, Aadhaar, Transfer No., Package No., Seal No., Batch ID…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onPressEnter={handleSearch}
            prefix={<SearchOutlined />}
          />
          <Button size="large" type="primary" onClick={handleSearch} loading={loading}>
            Search
          </Button>
        </Space.Compact>
        <Text type="secondary" style={{ fontSize: 12, marginTop: 8, display: 'block' }}>
          Supports: case numbers (WIC-xxx, PRC-xxx), transfer numbers (TRANS-xxx), package (PKG-xxx), seal (SEAL-xxx), batch (INVB-xxx), customer name, phone, Aadhaar
        </Text>
      </Card>

      <Spin spinning={loading}>
        {searched && results.length === 0 && !loading && (
          <Empty description="No results found" />
        )}
        {results.length > 0 && (
          <List
            dataSource={results}
            renderItem={(item) => (
              <List.Item
                key={item.id}
                style={{ cursor: item.type !== 'CUSTOMER' ? 'pointer' : 'default' }}
                onClick={() => handleNavigate(item)}
                actions={[
                  item.type !== 'CUSTOMER' && (
                    <Button size="small" type="link" onClick={() => handleNavigate(item)}>
                      View Timeline
                    </Button>
                  ),
                ].filter(Boolean)}
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color={TYPE_COLOR[item.type] || 'default'}>{TYPE_LABEL[item.type] || item.type}</Tag>
                      <Text strong>{item.label}</Text>
                    </Space>
                  }
                  description={
                    item.type === 'CUSTOMER'
                      ? `${item.data?.name} — ${item.data?.phone}`
                      : item.type === 'WALK_IN_CASE' || item.type === 'PLEDGE_CASE'
                        ? `Branch: ${item.data?.branch?.name || '—'} | Status: ${item.data?.status || '—'} | Vault: ${item.data?.inventoryStatus || 'PENDING'}`
                        : item.type === 'TRANSFER'
                          ? `Source: ${item.data?.sourceBranch?.name || '—'} | Status: ${item.data?.status}`
                          : null
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Spin>
    </div>
  );
}
